import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl'
import Dropdown from 'react-dropdown'
import memoize from 'memoize-one'

import { formatDateTime, formatToDateTime } from 'utils'

import moment from 'moment-timezone/builds/moment-timezone-with-data'

import EventPaymentInstructions from './EventPaymentInstructions'
import ReservationDatePicker from './ReservationDatePicker'

class ReservationDateRequest extends React.Component {
  UNSAFE_componentWillMount() {
    const { selected, event, reservation } = this.props
    const { date, time } = this.getInitialDateTime()

    if (date && time) {
      const formattedDatetime = formatDateTime(
        Object.assign({}, { date, time }),
        reservation.data.timezone || event.location.timezone
      )
      selected(formattedDatetime, 'propose')
    }
    this.setState({ date, time })

    this.getMomentDates = memoize((dates, timezone) => {
      const momentDates = dates.map((dateObject) => Object.assign(
        {},
        { m: moment.tz(dateObject.d, timezone) },
        dateObject
      ))

      return momentDates
    })

    this.getThisDate = memoize((start, momentDates, timezone) => {
      const selectedDate = moment.tz(start, timezone)
      const selectedDateString = selectedDate.format()

      return momentDates
        .find(({ m }) =>
          selectedDateString === m.format()
        ) || { m: selectedDate }
    })

    this.dateSelected = this.dateSelected.bind(this)
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { event, reservation } = nextProps
    const { start: selectedDate } = reservation.data
    const { timezone } = event.location
    const state = formatToDateTime(selectedDate, reservation.data.timezone || timezone)

    this.setState(state)
  }

  getInitialDateTime() {
    const { event, reservation } = this.props
    const { start: selectedDate } = reservation.data
    const { location, dates } = event
    const { timezone } = location

    if (selectedDate) {
      return formatToDateTime(selectedDate, reservation.data.timezone || timezone)
    } else if (dates.length) {
      return formatToDateTime(dates[0].d, reservation.data.timezone || timezone)
    }

    return { date: '', time: '' }
  }

  getTimeEntries(date, momentDates) {
    const { event, reservation } = this.props
    const timezone = reservation.data.timezone || event.location.timezone
    const timeEntries = {
      realtime: [],
      proposal: [],
      total: [],
      entries: 0,
    }

    const selectedDayString = moment
      .tz(date, timezone)
      .format('YYYY-MM-DD')
    const dayDateTimes = momentDates.filter(
      ({ m }) => {
        return m.format('YYYY-MM-DD') === selectedDayString
      }
    )
    dayDateTimes.forEach(({ m, rt }) => {
      const value = m.format('HH:mm')
      // TODO: remove label once Dropdown is updated to fix missing label placeholder
      const timeObject = { value, label: value }
      if (rt) {
        timeEntries.realtime.push(timeObject)
      } else {
        timeEntries.proposal.push(timeObject)
      }
    })

    timeEntries.proposal = timeEntries.proposal
      .sort((a, b) => {
        return (new Date(`1970-01-01T${a.value}`) - new Date(`1970-01-01T${b.value}`))
      })
    timeEntries.total = timeEntries.proposal.concat(timeEntries.realtime)
      .sort((a, b) => {
        return (new Date(`1970-01-01T${a.value}`) - new Date(`1970-01-01T${b.value}`))
      })
    timeEntries.proposal = Array.from(new Set(timeEntries.proposal))
    timeEntries.entries = timeEntries.total.length
    return timeEntries
  }

  dateSelected(date, modifiers) {
    if (modifiers.disabled) {
      return
    }

    let { time } = this.state
    const { selected, event, reservation } = this.props
    const { dates, location } = event
    const timezone = reservation.data.timezone || location.timezone
    const momentDates = this.getMomentDates(dates, timezone)
    const times = this.getTimeEntries(date, momentDates).total
    const checkTime = times.find(({ value }) => value === time)
    if (!checkTime) {
      time = times[0].value || ''
    }

    this.setState({ date, time })
    const formattedDatetime = formatDateTime(
      Object.assign({}, { date, time }),
      reservation.data.timezone || timezone
    )
    selected(formattedDatetime, 'propose')
  }

  timeSelected(timeData) {
    const { selected, event, reservation } = this.props
    const { timezone } = event.location
    const time = timeData.value

    this.setState({ time })
    const formattedDatetime = formatDateTime(
      Object.assign({}, this.state, { time }),
      reservation.data.timezone || timezone
    )
    selected(formattedDatetime, 'propose')
  }

  render() {
    const { date } = this.state
    const { locale, reservation, event } = this.props
    const timezone = reservation.data.timezone || event.location.timezone

    const momentDates = this.getMomentDates(event.dates, timezone)
    const thisDate = this.getThisDate(reservation.data.start, momentDates, timezone)
    const timeEntries = this.getTimeEntries(date, momentDates)

    return (
      <div className="event-request">
        <div className="grid--full">
          <div className="grid__item desk-6-12">
            <div className="event-request__date">
              <ReservationDatePicker
                date={date}
                dates={momentDates}
                timezone={timezone}
                locale={locale}
                dateSelected={this.dateSelected}
              />
            </div>
          </div>
          <div className="grid__item desk-6-12">
            {
              date && timeEntries.entries ?
                <div className="event-request__hour">
                  <div className="dropdown__label">
                    <FormattedMessage id="app.event.reservation.time" />
                  </div>
                  <div className="dropdown__input">
                    <Dropdown
                      options={timeEntries.total}
                      onChange={this.timeSelected.bind(this)}
                      value={thisDate.m.format('HH:mm')}
                    />
                  </div>
                  <div>
                    <FormattedHTMLMessage
                      id={
                        thisDate.rt
                          ? 'app.event.reservation.instructions.programmed-date.description'
                          : 'app.event.reservation.instructions.requested-date.description'
                      }
                    />
                  </div>
                  {
                    !event.hidePrice &&
                    <EventPaymentInstructions
                      paymentDisabled={!thisDate.pay}
                      payment={event.payment}
                      currency={event.location.currency}
                      price={thisDate.pri || event.price.value}
                      priceMode={event.price.mode}
                      guests={reservation.data.guests}
                      coupon={reservation.coupon}
                      eventExtras={event.extras}
                      reservationExtras={reservation.data.extras}
                    />
                  }
                </div>
                :
                <div className="event-request__hour">
                  <FormattedMessage id="app.event.reservation.date-selection.placeholder" />
                </div>
            }
          </div>
        </div>
      </div>
    )
  }
}

ReservationDateRequest.propTypes = {
  selected: PropTypes.func.isRequired,
  selectedDate: PropTypes.string,
  locale: PropTypes.string.isRequired,
  event: PropTypes.object.isRequired,
  reservation: PropTypes.object.isRequired,
}

export default ReservationDateRequest
